import { createHttpClient } from '../../../api/http';
import { serviceUrls } from '../../../config/serviceUrls';
import { dedupeRuntimeQuotaFetch } from './runtimeQuotaDedupe';
import { RUNTIME_START_CLIENT_TIMEOUT_MS } from './runtimeSessionStartReconciliation';

/** Quota GET timeout — same numeric value as Start until Phase 1 HAR p95 is remeasured. */
export const RUNTIME_QUOTA_TIMEOUT_MS = 18_000;

const quotaClient = createHttpClient(serviceUrls.gateway, {
  timeoutMs: RUNTIME_QUOTA_TIMEOUT_MS,
});

const sessionClient = createHttpClient(serviceUrls.gateway, {
  timeoutMs: RUNTIME_START_CLIENT_TIMEOUT_MS,
});

export type RuntimeSessionStatus =
  | 'ACTIVE'
  | 'ENDED'
  | 'EXPIRED'
  | 'IDLE_TIMEOUT'
  | 'FAILED';

export type RuntimeActiveSession = {
  session_id: string;
  student_id?: string;
  tenant_id?: string;
  school_id?: string;
  feature_name?: string;
  started_at?: string | null;
  expires_at?: string | null;
  ended_at?: string | null;
  status?: RuntimeSessionStatus | string;
  max_session_minutes?: number | null;
  duration_seconds?: number | null;
  termination_reason?: string | null;
  session_remaining_seconds?: number | null;
  idle_remaining_seconds?: number | null;
  idle_warn?: boolean;
};

export type RuntimeQuotaResponse = {
  ok?: boolean;
  runtime_version?: number;
  feature_name: string;
  governance_mode?: 'flexible' | 'strict' | string;
  max_session_minutes: number;
  monthly_budget_minutes: number | null;
  monthly_used_minutes: number;
  monthly_remaining_minutes: number | null;
  monthly_budget_seconds?: number | null;
  monthly_used_seconds?: number;
  monthly_remaining_seconds?: number | null;
  quota_exhausted?: boolean;
  active_session: RuntimeActiveSession | null;
  session_expires_at?: string | null;
  session_remaining_seconds?: number | null;
  idle_timeout_seconds?: number;
  idle_warn_seconds?: number;
};

export type EphemeralTokenStatus = 'issued' | 'unavailable' | 'skipped';

export type RuntimeSessionStartResponse = RuntimeActiveSession & {
  ok?: boolean;
  monthly_budget_minutes?: number | null;
  monthly_used_minutes?: number;
  monthly_remaining_minutes?: number | null;
  governance_mode?: string;
  ephemeral_token?: string | null;
  ephemeral_token_expire_time?: string | number | null;
  ephemeral_token_model?: string | null;
  ephemeral_token_status?: EphemeralTokenStatus;
};

export type { EmbeddedRuntimeToken } from './runtimeStartEmbeddedToken';
export { extractEmbeddedTokenFromRuntimeStart } from './runtimeStartEmbeddedToken';

/**
 * Merge an ACTIVE runtime session (start/resume) into quota state.
 * Always returns a full object — never leave React state null when a session exists.
 */
export function applyRuntimeSessionToQuota(
  prev: RuntimeQuotaResponse | null,
  session: RuntimeActiveSession | RuntimeSessionStartResponse,
  featureName: string,
): RuntimeQuotaResponse {
  const started = session as RuntimeSessionStartResponse;
  return {
    ok: true,
    runtime_version: prev?.runtime_version ?? 2,
    feature_name: prev?.feature_name ?? featureName,
    governance_mode: started.governance_mode ?? prev?.governance_mode,
    max_session_minutes:
      started.max_session_minutes ?? prev?.max_session_minutes ?? 0,
    monthly_budget_minutes:
      started.monthly_budget_minutes ?? prev?.monthly_budget_minutes ?? null,
    monthly_used_minutes:
      started.monthly_used_minutes ?? prev?.monthly_used_minutes ?? 0,
    monthly_remaining_minutes:
      started.monthly_remaining_minutes ?? prev?.monthly_remaining_minutes ?? null,
    monthly_budget_seconds: prev?.monthly_budget_seconds ?? null,
    monthly_used_seconds: prev?.monthly_used_seconds ?? 0,
    monthly_remaining_seconds: prev?.monthly_remaining_seconds ?? null,
    quota_exhausted: false,
    active_session: session,
    session_expires_at: session.expires_at ?? null,
    session_remaining_seconds: session.session_remaining_seconds ?? null,
    idle_timeout_seconds: prev?.idle_timeout_seconds,
    idle_warn_seconds: prev?.idle_warn_seconds,
  };
}

/** Session remaining seconds from backend expires_at (display only; never invent local caps). */
export function sessionRemainingSecondsFromExpiresAt(
  expiresAt: string | null | undefined,
  nowMs: number = Date.now(),
): number | null {
  if (!expiresAt) return null;
  const t = Date.parse(expiresAt);
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((t - nowMs) / 1000));
}

export function formatSessionCountdown(totalSeconds: number | null | undefined): string {
  if (totalSeconds == null || !Number.isFinite(totalSeconds)) return '—';
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

export async function fetchRuntimeQuota(
  featureName: string,
  opts?: { cacheBust?: boolean; signal?: AbortSignal },
): Promise<RuntimeQuotaResponse> {
  return dedupeRuntimeQuotaFetch(
    featureName,
    async () => {
      const res = await quotaClient.get<RuntimeQuotaResponse>('/v1/tutor/runtime/quota', {
        params: { feature_name: featureName },
        signal: opts?.signal,
      });
      return res.data;
    },
    opts,
  );
}

export async function fetchRuntimeActiveSession(
  featureName?: string,
): Promise<RuntimeActiveSession | null> {
  const res = await sessionClient.get<{ ok?: boolean; active_session: RuntimeActiveSession | null }>(
    '/v1/tutor/runtime/session/active',
    { params: featureName ? { feature_name: featureName } : undefined },
  );
  return res.data?.active_session ?? null;
}

export async function startRuntimeSession(body: {
  feature_name: string;
  tutor_type?: string;
  mode?: string;
  lesson_no?: number;
  activity?: string | null;
  idempotency_key?: string;
}): Promise<RuntimeSessionStartResponse> {
  const res = await sessionClient.post<RuntimeSessionStartResponse>('/v1/tutor/runtime/session/start', body);
  return res.data;
}

export async function endRuntimeSession(body: {
  session_id: string;
  termination_reason?:
    | 'student_end'
    | 'expires_at'
    | 'idle_timeout'
    | 'teacher_end'
    | 'fatal_error';
  tokens_used?: number;
  estimated_cost?: number;
}): Promise<RuntimeActiveSession> {
  const res = await sessionClient.post<RuntimeActiveSession>('/v1/tutor/runtime/session/end', body);
  return res.data;
}

export async function touchRuntimeSession(sessionId: string): Promise<RuntimeActiveSession> {
  const res = await sessionClient.post<RuntimeActiveSession>('/v1/tutor/runtime/session/touch', {
    session_id: sessionId,
  });
  return res.data;
}
